import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import {
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaClock,
    FaCheckCircle,
    FaExclamationTriangle,
    FaPaperPlane
} from 'react-icons/fa';
import { useLanguage } from '../i18n';

type ContactFormValues = {
    name: string;
    email: string;
    phone?: string;
    serviceType: string;
    message: string;
};

const Contact = () => {
    const { language, t } = useLanguage();
    const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm<ContactFormValues>();

    const onSubmit = async (data: ContactFormValues) => {
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Simulate API call - replace with your actual form submission logic
            console.log('Form submitted:', data);

            // Simulate network delay
            await new Promise(resolve => setTimeout(resolve, 2000));

            // For demo purposes, we'll always show success
            setSubmitStatus('success');
            reset();

            // You can replace this with actual form submission:
            // const response = await fetch('/api/contact', {
            //   method: 'POST',
            //   headers: { 'Content-Type': 'application/json' },
            //   body: JSON.stringify(data)
            // });
            // 
            // if (response.ok) {
            //   setSubmitStatus('success');
            //   reset();
            // } else {
            //   setSubmitStatus('error');
            // }

        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const serviceOptions = language === 'vi' ? [
        'Phát triển Website WordPress', 'Theme WordPress Tùy chỉnh', 'Bảo trì Website', 'Thiết kế lại Website', 'Khác'
    ] : [
        'WordPress website development', 'Custom WordPress theme', 'Website maintenance', 'Website redesign', 'Other'
    ];

    const contactInfo = [
        {
            icon: <FaPhone className="text-2xl text-primary-600" />,
            title: language === 'vi' ? "Điện thoại" : "Phone",
            details: ["+84 (0) 918 873 984 - Mr. Duc", language === 'vi' ? "T2-T6 9AM-9PM" : "Mon-Fri 9AM-9PM"]
        },
        {
            icon: <FaEnvelope className="text-2xl text-primary-600" />,
            title: "Email",
            details: ["hongducmmo@gmail.com", language === 'vi' ? "Chúng tôi phản hồi trong 24h" : "We reply within 24 hours"]
        },
        {
            icon: <FaMapMarkerAlt className="text-2xl text-primary-600" />,
            title: language === 'vi' ? "Văn phòng" : "Office",
            details: ["318/47 Phan Văn Trị, P.11, Bình Thạnh, HCMC"]
        },
        {
            icon: <FaClock className="text-2xl text-primary-600" />,
            title: language === 'vi' ? "Giờ làm việc" : "Working hours",
            details: [language === 'vi' ? "Thứ 2 - Thứ 6: 9AM - 6PM" : "Mon - Fri: 9AM - 6PM", language === 'vi' ? "Thứ 7: 10AM - 4PM" : "Sat: 10AM - 4PM"]
        }
    ];

    return (
        <section id="contact" className="py-20 bg-gray-50">
            <div className="container mx-auto px-4">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        {t('contactTitleLead')}<span className="text-primary-600">{t('contactTitleAccent')}</span>{t('contactTitleTail')}
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        {t('contactDescription')}
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Contact Form */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="bg-white rounded-3xl p-8 shadow-lg"
                    >
                        <h3 className="text-2xl font-bold text-gray-900 mb-6">
                            {t('sendMessage')}
                        </h3>

                        {submitStatus === 'success' && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg flex items-center"
                            >
                                <FaCheckCircle className="text-green-600 mr-3" />
                                <div>
                                    <p className="text-green-800 font-medium">{t('messageSent')}</p>
                                    <p className="text-green-700 text-sm">{t('replyWithin24')}</p>
                                </div>
                            </motion.div>
                        )}

                        {submitStatus === 'error' && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg flex items-center"
                            >
                                <FaExclamationTriangle className="text-red-600 mr-3" />
                                <div>
                                    <p className="text-red-800 font-medium">{t('sendError')}</p>
                                    <p className="text-red-700 text-sm">{t('tryAgain')}</p>
                                </div>
                            </motion.div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            {/* Name Field */}
                            <div>
                                    <label className="block text-gray-700 font-medium mb-2">
                                    {t('fullName')}
                                </label>
                                <input
                                    type="text"
                                    {...register('name', {
                                        required: language === 'vi' ? 'Tên là bắt buộc' : 'Name is required',
                                        minLength: { value: 2, message: language === 'vi' ? 'Tên phải có ít nhất 2 ký tự' : 'Name must be at least 2 characters' }
                                    })}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-300' : 'border-gray-300'
                                        } focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors`}
                                    placeholder={language === 'vi' ? 'Nhập họ và tên của bạn' : 'Enter your full name'}
                                />
                                {errors.name && (
                                    <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
                                )}
                            </div>

                            {/* Email Field */}
                            <div>
                                    <label className="block text-gray-700 font-medium mb-2">
                                    {t('email')}
                                </label>
                                <input
                                    type="email"
                                    {...register('email', {
                                        required: language === 'vi' ? 'Email là bắt buộc' : 'Email is required',
                                        pattern: {
                                            value: /^\S+@\S+$/i,
                                            message: language === 'vi' ? 'Địa chỉ email không hợp lệ' : 'Invalid email address'
                                        }
                                    })}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-300' : 'border-gray-300'
                                        } focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors`}
                                    placeholder={language === 'vi' ? 'Nhập địa chỉ email của bạn' : 'Enter your email address'}
                                />
                                {errors.email && (
                                    <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>
                                )}
                            </div>

                            {/* Phone Field */}
                            <div>
                                    <label className="block text-gray-700 font-medium mb-2">
                                    {t('phone')}
                                </label>
                                <input
                                    type="tel"
                                    {...register('phone', {
                                        pattern: {
                                            value: /^[\+]?[1-9][\d]{0,15}$/,
                                            message: language === 'vi' ? 'Số điện thoại không hợp lệ' : 'Invalid phone number'
                                        }
                                    })}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-300' : 'border-gray-300'
                                        } focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors`}
                                    placeholder={language === 'vi' ? 'Nhập số điện thoại của bạn' : 'Enter your phone number'}
                                />
                                {errors.phone && (
                                    <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>
                                )}
                            </div>

                            {/* Service Type Field */}
                            <div>
                                    <label className="block text-gray-700 font-medium mb-2">
                                    {t('serviceType')}
                                </label>
                                <select
                                    {...register('serviceType', { required: language === 'vi' ? 'Vui lòng chọn loại dịch vụ' : 'Please choose a service' })}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.serviceType ? 'border-red-300' : 'border-gray-300'
                                        } focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors`}
                                >
                                    <option value="">{t('chooseService')}</option>
                                    {serviceOptions.map((service, index) => (
                                        <option key={index} value={service}>
                                            {service}
                                        </option>
                                    ))}
                                </select>
                                {errors.serviceType && (
                                    <p className="text-red-600 text-sm mt-1">{errors.serviceType.message}</p>
                                )}
                            </div>

                            {/* Message Field */}
                            <div>
                                    <label className="block text-gray-700 font-medium mb-2">
                                    {t('projectDetails')}
                                </label>
                                <textarea
                                    {...register('message', {
                                        required: language === 'vi' ? 'Tin nhắn là bắt buộc' : 'Message is required',
                                        minLength: { value: 10, message: language === 'vi' ? 'Tin nhắn phải có ít nhất 10 ký tự' : 'Message must be at least 10 characters' }
                                    })}
                                    rows={5}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.message ? 'border-red-300' : 'border-gray-300'
                                        } focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors resize-vertical`}
                                    placeholder={language === 'vi' ? 'Hãy cho chúng tôi biết về yêu cầu dự án, thời gian và các tính năng cụ thể bạn cần...' : 'Tell us about your project requirements, timeline, and the features you need...'}
                                />
                                {errors.message && (
                                    <p className="text-red-600 text-sm mt-1">{errors.message.message}</p>
                                )}
                            </div>

                            {/* Submit Button */}
                            <motion.button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all flex items-center justify-center ${isSubmitting
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-primary-600 hover:bg-primary-700 text-white'
                                    }`}
                                whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                        {t('sending')}
                                    </>
                                ) : (
                                    <>
                                        <FaPaperPlane className="mr-2" />
                                        {t('send')}
                                    </>
                                )}
                            </motion.button>
                        </form>

                        <p className="text-sm text-gray-500 mt-4 text-center">
                            {t('requiredNote')}
                        </p>
                    </motion.div>

                    {/* Contact Info */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="space-y-8"
                    >
                        <div className="bg-white rounded-3xl p-8 shadow-lg">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">
                                {t('getInTouch')}
                            </h3>
                            <div className="space-y-6">
                                {contactInfo.map((info, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1, duration: 0.5 }}
                                        className="flex items-start space-x-4"
                                    >
                                        <div className="flex-shrink-0 mt-1">{info.icon}</div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1">{info.title}</h4>
                                            {info.details.map((detail, detailIndex) => (
                                                <p key={detailIndex} className="text-gray-600 text-sm">
                                                    {detail}
                                                </p>
                                            ))}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Quick Response Time */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-3xl p-8 text-white"
                        >
                            <h3 className="text-xl font-bold mb-4">{t('whyUs')}</h3>
                            <ul className="space-y-3">
                                {[
                                    ...(language === 'vi' ? ["Thời gian phản hồi nhanh trong 24 giờ", "Tư vấn và báo giá miễn phí", "Hơn 5 năm chuyên môn WordPress", "Giá cả minh bạch, không phí ẩn", "Hỗ trợ và bảo trì liên tục"] : ["Fast response within 24 hours", "Free consultation and quotes", "5+ years of WordPress expertise", "Transparent pricing, no hidden fees", "Ongoing support and maintenance"])
                                ].map((item, index) => (
                                    <li key={index} className="flex items-start space-x-3">
                                        <FaCheckCircle className="text-primary-200 mt-0.5 flex-shrink-0" />
                                        <span className="text-primary-50">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Emergency Contact */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="bg-red-50 border border-red-200 rounded-2xl p-6"
                        >
                            <div className="flex items-start space-x-3">
                                <FaExclamationTriangle className="text-red-600 text-xl mt-1" />
                                <div>
                                    <h4 className="font-semibold text-red-800 mb-2">{t('emergency')}</h4>
                                    <p className="text-red-700 text-sm mb-3">
                                        {language === 'vi' ? 'Nếu website của bạn bị sập hoặc bị xâm phạm, hãy gọi đường dây khẩn cấp của chúng tôi:' : 'If your website is down or compromised, call our emergency line:'}
                                    </p>
                                    <a
                                        href="tel:+84918873984"
                                        className="text-red-600 font-bold hover:text-red-700 transition-colors"
                                    >
                                        +84 (0) 918 873 984
                                    </a>
                                    <p className="text-red-600 text-xs mt-1">{language === 'vi' ? 'Hỗ trợ 24/7 cho các vấn đề nghiêm trọng' : '24/7 support for critical issues'}</p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
